import React from 'react';
import { Box, HStack, VStack } from '@chakra-ui/react';
import PdfNavigationControls from '../shared/PdfNavigationControls';
import PdfDocumentRenderer from '../shared/PdfDocumentRenderer';
import ViewerExpandButton from '../shared/ViewerExpandButton.tsx';
import { ViewerFullViewerProps } from '../shared/types';

/**
 * Full PDF viewer component for standard document viewing
 *
 * Provides traditional PDF viewer layout with top navigation controls,
 * gray styling, and click-to-expand functionality for detailed document review.
 */
const PdfFullViewer: React.FC<ViewerFullViewerProps> = ({
	numPages,
	pageNumber,
	loading,
	pdfOptions,
	onDocumentLoadSuccess,
	onDocumentLoadError,
	goToPrevPage,
	goToNextPage,
	onExpand,
	showPageControls = true,
}) => {
	return (
		<VStack gap={4} w="full">
			{showPageControls && (
				<HStack justify="space-between" w="full" px={0}>
					<PdfNavigationControls
						pageNumber={pageNumber}
						numPages={numPages}
						loading={loading}
						onPrevPage={goToPrevPage}
						onNextPage={goToNextPage}
						variant="full"
						showPageInfo={true}
					/>
				</HStack>
			)}

			{/* PDF Viewer */}
			<Box
				layerStyle="card"
				w="full"
				overflow="hidden"
				p={4}
				onClick={onExpand}
				_hover={{ cursor: 'pointer' }}
				className="group" // This is for hover effects on child elements
				// see  https://chakra-ui.com/docs/styling/conditional-styles#group-selectors
				position="relative"
			>
				{/* Expand Button */}
				<ViewerExpandButton onExpand={onExpand} loading={loading} top={2} right={2} />

				<PdfDocumentRenderer
					pdfOptions={pdfOptions}
					pageNumber={pageNumber}
					width={400}
					loading={loading}
					onLoadSuccess={onDocumentLoadSuccess}
					onLoadError={onDocumentLoadError}
					containerProps={{ h: loading ? '400px' : 'auto' }}
					spinnerSize="lg"
				/>
			</Box>
		</VStack>
	);
};

export default PdfFullViewer;
